import { FormApi } from 'final-form';
import { observer } from 'mobx-react';
import React, { FunctionComponent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { EventFormData } from '~admin/view/components/details/event-form-data';
import {
  disableOfferGroupDeleteButton,
  enableOfferSuggestions,
} from '~admin/view/components/details/offer-group-details/offer-group-fields.helper';
import { OfferGroupSectionPicker } from '~admin/view/components/details/offer-group-details/offer-group-section-picker.component';
import {
  OfferGroupSuggestedIncrements,
  SuggestedIncrementsFormData,
} from '~admin/view/components/details/offer-group-details/offer-group-suggested-increments.component';
import { SelectField } from '~admin/view/components/ui/form/fields/field-select.component';
import { TextField } from '~common/components/form/field-text.component';
import {
  TranslationField,
  TranslationFormData,
} from '~admin/view/components/ui/form/fields/field-translation.component';
import { MarketplaceType } from '~client/model/marketplace-type';
import { SeatConfigurationType } from '~client/model/seat-configuration-type';
import { Box } from '~common/components/box.component';
import { Button } from '~common/components/button/button.component';
import { Size } from '~common/components/button/button.helper';
import { Column } from '~common/components/column.component';
import { optionEnumeratorMapper } from '~common/components/select.helper';
import { Color } from '~common/model/basics';
import { DeleteIcon } from '~web/view/components/ui/icon/delete.icon';
import {
  OfferGroupPriceFormData,
  OfferGroupPrices,
} from '~admin/view/components/details/offer-group-details/offer-group-prices.component';

export interface OfferGroupFormData {
  name: TranslationFormData<string>[];
  prices: OfferGroupPriceFormData[];
  additionalDetails: TranslationFormData<string>[];
  capacityDefaultString: TranslationFormData<string>[];
  seatConfigurationType: SeatConfigurationType;
  sections: number[];
  rowRange: string;
  seatRange: string;
  suggestedIncrements: SuggestedIncrementsFormData[];
}

interface OfferGroupFieldsProps {
  name: string;
  marketplaceType: MarketplaceType;
  offerGroupIndex: number;
  onRemove?: (index: number) => void;
  form?: FormApi<EventFormData>;
}

export const OfferGroupFields: FunctionComponent<OfferGroupFieldsProps> = observer(
  ({ name, marketplaceType, offerGroupIndex, onRemove, form }) => {
    const { t } = useTranslation();

    return (
      <Container>
        <Top>
          <DeleteButton
            color={Color.White}
            size={Size.Small}
            disabled={disableOfferGroupDeleteButton(marketplaceType, offerGroupIndex)}
            onClick={(): void => {
              onRemove?.(offerGroupIndex);
            }}
          >
            <DeleteIcon color={Color.Black} />
          </DeleteButton>
        </Top>
        <Row>
          <RestrictBox>
            <TranslationField
              name={`${name}.name`}
              label={t('createEvent.offerGroupDetails.nameLabel')}
              render={(renderProps): ReactNode => <TextField {...renderProps} />}
            />
          </RestrictBox>
        </Row>
        <Row>
          <RestrictBox>
            <TranslationField
              name={`${name}.additionalDetails`}
              label={t('createEvent.offerGroupDetails.additionalDetails')}
              render={(renderProps): ReactNode => <TextField multiline {...renderProps} />}
            />
          </RestrictBox>
        </Row>
        <Row>
          <RestrictBox>
            <TranslationField
              name={`${name}.capacityDefaultString`}
              label={t('createEvent.offerGroupDetails.capacityDefaultString')}
              render={(renderProps): ReactNode => <TextField {...renderProps} />}
            />
          </RestrictBox>
        </Row>
        <OfferGroupPrices fieldArrayName={name} marketplaceType={marketplaceType} form={form} />
        <Row>
          <RestrictBox>
            <SelectField
              name={`${name}.seatConfigurationType`}
              label={t('createEvent.offerGroupDetails.seatConfigurationType.label')}
              options={optionEnumeratorMapper(
                SeatConfigurationType,
                t,
                'createEvent.offerGroupDetails.seatConfigurationType.enum',
              )}
            />
          </RestrictBox>
        </Row>
        <OfferGroupSectionPicker
          name={name}
          label={t('createEvent.offerGroupDetails.sectionLabel')}
          form={form}
        />
        {enableOfferSuggestions(marketplaceType) && (
          <>
            <Divider />
            <OfferGroupSuggestedIncrements name={name} />
          </>
        )}
      </Container>
    );
  },
);

const Container = styled(Column)`
  flex: 1 1 auto;
  border-width: 2px;
  border-color: ${(props): string => props.theme.colors.gray.v3};
  border-style: solid hidden;
  padding: 16px 16px 32px;
  margin-bottom: 18px;

  > div:not(:first-child) {
    margin-top: 16px;
  }
`;

const Row = styled(Box)`
  width: 100%;
`;

const Top = styled(Box)`
  justify-content: flex-end;
  margin: 0 12px;
`;

const RestrictBox = styled(Box)`
  flex: 1 1 auto;
  margin: 0 12px;
`;

const DeleteButton = styled(Button)`
  && {
    min-width: 44px;
    width: 44px;
    height: 44px;
    padding: 0;
  }
`;

const Divider = styled.div`
  width: 100%;
  height: 2px;
  background: ${(props): string => props.theme.colors.gray.v3};
`;



// WEBPACK FOOTER //
// ./view/components/details/offer-group-details/offer-group-fields.component.tsx